// TICK, ported from internal/wire/tick.go.
//
// The frame boundary: one message per tick to every connected client, carrying
// the tick number and a bitmap of every chunk that changed in it.
//
//	u16 dirtyChunkCount
//	[ChunkSetBytes] dirty bitmap
//
// It exists because continuity cannot be judged on DELTA. A chunk with no changes
// in a tick emits no DELTA, so a subscriber to a quiet chunk sees tick 10 then
// tick 12 and cannot tell that apart from a lost frame. With TICK the two cases
// separate cleanly: absent from the bitmap means quiet, present in the bitmap
// with no DELTA means lost.
//
// dirtyChunkCount is redundant with the bitmap and is checked against it. The two
// disagreeing means the sender's dirty set and its bitmap parted company, which
// is worth catching at the seam rather than as chunks that never repaint.

import { Reader, Writer } from './bytes';
import { ChunkSet, ChunkSetBytes } from './chunkset';
import { ClassTick } from './classes';
import { errDirtyBitmap, errDirtyCount } from './errors';
import type { TickNo } from './core';

/** The fixed body width. */
export const TickBodySize = 2 + ChunkSetBytes;

export interface Tick {
  tick: TickNo;
  /** Every chunk that changed this tick, board-wide. */
  dirty: ChunkSet;
}

export function decodeTick(b: Uint8Array): Tick {
  const r = new Reader(b);
  const tick = r.header(ClassTick);
  const n = r.u16();
  const bm = r.copy(ChunkSetBytes);
  r.done();
  const dirty = new ChunkSet(bm);
  if (!dirty.canonical()) throw errDirtyBitmap();
  if (dirty.count !== n) throw errDirtyCount();
  return { tick, dirty };
}

/**
 * The encoder is server-side, and exists here only so a test can build a frame
 * the decoder is then run against without reaching for a fixture file.
 */
export function encodeTick(t: Tick): Uint8Array {
  if (!t.dirty.canonical()) throw errDirtyBitmap();
  return new Writer(9 + TickBodySize)
    .header(ClassTick, t.tick)
    .u16(t.dirty.count)
    .bytes(t.dirty.bits)
    .finish();
}
